import { useState } from 'react';
import { Badge, Button, Collapse, Modal, Table } from 'react-bootstrap';
import BookingTimeline from './BookingTimeline';
import BookingActions from './BookingActions';
import DeliveryTicketPanel from './DeliveryTicketPanel';
import InfoTip from './InfoTip';
import MapLink from './MapLink';
import {
  DEPOSIT_STATUS_HELP, PAYOUT_STATUS_HELP, PAYMENT_STATE_HELP,
  RETURN_STATUS_HELP, DELIVERY_STATUS_HELP, RECONCILIATION_HELP
} from './statusHelp';

const STATUS_VARIANT = { confirmed: 'success', cancelled: 'danger', pending: 'warning' };
const money = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
// Amounts the customer pays (safe for every role). The website's cut of the deal
// (commissionRate / ownerEarnings / platformCommission) is NOT here - it never reaches a
// customer/delivery response (BookingRepository.toRoleView) and is shown in its own clearly
// labelled "Business breakdown" section below, owner/admin/finance only.
const CHARGE_FIELDS = ['rentalAmount', 'platformFee', 'deliveryCharge', 'totalAmount'];
const BUSINESS_FIELDS = ['commissionRate', 'ownerEarnings', 'platformCommission'];

const PAYMENT_STATE_VARIANT = {
  RECEIVED: 'success',
  PARTIALLY_RECEIVED: 'info',
  RECEIVABLE: 'warning',
  PENDING_CONFIRMATION: 'warning',
  FAILED: 'danger',
  REFUNDED: 'secondary',
  PARTIALLY_REFUNDED: 'secondary'
};

const REFUND_RESPONSIBILITY_LABEL = {
  RENTAL_OWNER: 'Rental owner',
  RENTALVERSE_FINANCE: 'RentalVerse Finance (website business)'
};

const COLLECTION_SOURCE_LABEL = {
  OWNER_CASH: 'Cash to rental owner',
  OWNER_DIRECT_ONLINE: 'Online, direct to rental owner',
  PLATFORM_ONLINE: 'Online, to RentalVerse business account'
};

const TAX_LINE_LABEL = {
  RENTAL: 'Rental',
  PLATFORM_FEE: 'Platform fee',
  DELIVERY: 'Delivery',
  PLATFORM_COMMISSION: 'Booking commission'
};
const gstOf = (l) => (l.igst > 0 ? `IGST ${money(l.igst)}` : `CGST ${money(l.cgst)} + SGST ${money(l.sgst)}`);

function formatAddress(addr) {
  if (!addr) return null;
  // delivery_distance_pricing.md - prefer Google's own formatted string when the customer picked
  // the address from Maps search / "detect my location".
  if (addr.formatted) return addr.formatted;
  if (!addr.line1) return null;
  return [addr.line1, addr.line2, addr.area, addr.city, addr.pincode].filter(Boolean).join(', ');
}

// "Delivery charge" row annotation - what the tiered charge was based on (frozen at booking time).
function deliveryChargeNote(b) {
  if (b.deliveryChargeBasis === 'FREE_SHIPPING') return 'free shipping (owner arranges)';
  if (b.deliveryChargeBasis === 'FLAT_FALLBACK') return 'flat rate — distance unavailable';
  if (b.deliveryDistanceMeters != null) return `${(b.deliveryDistanceMeters / 1000).toFixed(1)} km`;
  return null;
}

// Fall back to the coarse paymentStatus string when the API hasn't sent the richer summary
// (older payloads, or a hand-built object in a test). A payment only reads as received when it
// carries a method AND a reference - so a bare "PAID" with no evidence is shown as unconfirmed.
function derivePaymentView(b) {
  if (b.paymentSummary) return b.paymentSummary;
  const s = b.paymentStatus;
  if (!s) return null;
  const confirmed = !!(b.payment?.method && (b.payment?.reference || b.payment?.gateway?.paymentId));
  const map = {
    PAID: confirmed
      ? { state: 'RECEIVED', label: 'Payment received' }
      : { state: 'PENDING_CONFIRMATION', label: 'Payment recorded - not confirmed (needs method & reference)' },
    PARTIALLY_PAID: confirmed
      ? { state: 'PARTIALLY_RECEIVED', label: 'Partially received' }
      : { state: 'PENDING_CONFIRMATION', label: 'Payment recorded - not confirmed' },
    PENDING: { state: 'RECEIVABLE', label: 'Payment receivable - not yet received' },
    AUTHORISED: { state: 'RECEIVABLE', label: 'Authorised - not yet received' },
    FAILED: { state: 'FAILED', label: 'Payment failed - receivable' },
    REFUNDED: { state: 'REFUNDED', label: 'Refunded' },
    PARTIALLY_REFUNDED: { state: 'PARTIALLY_REFUNDED', label: 'Partially refunded' }
  };
  return { ...(map[s] || { state: s, label: s }), amount: b.totalAmount };
}

// fix_issues3.md / fix_issue4.md - the single "open the full order in a popup" surface, reused
// everywhere an order id is shown. Renders defensively: every block is guarded on its field being
// present, which is the same as "hidden for this role" when the API omitted it - a customer never
// receives the business breakdown, a delivery account never receives any money field at all.
export default function OrderDetailsModal({
  booking, itemName, show, onHide, onViewProduct,
  // v3_fix_issues #12 - when `onChanged` is passed (OwnerDashboard / AdminDashboard), the
  // owner/admin booking-management actions render at the top of the popup. Omitted elsewhere
  // (customer / delivery / finance views), so the modal stays read-only there.
  onChanged, onRecordPayment, onMarkRefundSent, onViewCancellation, onAssignDelivery, onCancel,
  isAdmin = false
}) {
  // v3_fix_issues.md #1 - the tax & invoice breakdown is detail most people don't need every time,
  // so it's tucked behind a "Show tax & invoice details" toggle rather than always expanded.
  const [showTax, setShowTax] = useState(false);
  if (!booking) return null;
  const b = booking;
  const address = formatAddress(b.deliveryAddress);
  const pay = derivePaymentView(b);
  const hasCharges = CHARGE_FIELDS.some((f) => b[f] != null) || b.depositAmount != null;
  const hasBusiness = BUSINESS_FIELDS.some((f) => b[f] != null);
  const tax = b.taxBreakdown;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5">Order #{b._id.slice(-6).toUpperCase()}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {onChanged && (
          <div className="mb-3 pb-3 border-bottom">
            <BookingActions
              booking={b}
              isAdmin={isAdmin}
              onChanged={onChanged}
              onViewProduct={onViewProduct}
              onRecordPayment={onRecordPayment}
              onMarkRefundSent={onMarkRefundSent}
              onViewCancellation={onViewCancellation}
              onAssignDelivery={onAssignDelivery}
              onCancel={onCancel}
            />
          </div>
        )}
        <div className="d-flex flex-wrap gap-1 mb-3">
          {b.status && <Badge bg={STATUS_VARIANT[b.status] || 'secondary'}>{b.status}</Badge>}
          {pay && (
            <InfoTip text={PAYMENT_STATE_HELP[pay.state]}>
              <Badge bg={PAYMENT_STATE_VARIANT[pay.state] || 'secondary'}>{pay.label}</Badge>
            </InfoTip>
          )}
          {pay?.needsReconciliation && (
            <InfoTip text={RECONCILIATION_HELP}>
              <Badge bg="warning" text="dark">Awaiting finance reconciliation</Badge>
            </InfoTip>
          )}
          {/* "Owner payout" (platform -> rental owner), NOT the customer's payment above - only
              shown once it's actually due, so it can't read like it contradicts "Payment received". */}
          {b.payoutStatus && b.payoutStatus !== 'NOT_DUE' && (
            <InfoTip text={PAYOUT_STATUS_HELP[b.payoutStatus]}>
              <Badge bg="secondary">Owner payout: {b.payoutStatus.replace(/_/g, ' ').toLowerCase()}</Badge>
            </InfoTip>
          )}
          {b.depositAmount > 0 && b.depositStatus && (
            <InfoTip text={DEPOSIT_STATUS_HELP[b.depositStatus]}>
              <Badge bg="secondary">Deposit: {b.depositStatus.replace(/_/g, ' ')}</Badge>
            </InfoTip>
          )}
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <h6 className="text-uppercase text-muted small">Order</h6>
            <div className="small mb-1">
              <span className="text-muted">Full ID:</span>{' '}
              <span className="font-monospace">{b._id}</span>
            </div>
            {itemName && <div className="small mb-1">{itemName}</div>}
            {b.startDate && b.endDate && (
              <div className="small mb-1">
                {new Date(b.startDate).toLocaleDateString()} &ndash; {new Date(b.endDate).toLocaleDateString()}
              </div>
            )}
            {b.createdAt && (
              <div className="small text-muted">Booked {new Date(b.createdAt).toLocaleString()}</div>
            )}

            <h6 className="text-uppercase text-muted small mt-3">Customer</h6>
            <div className="small mb-1">{b.customerName || '—'}</div>
            {b.email && <div className="small text-muted">{b.email}</div>}
            {b.phoneNumber && <div className="small text-muted">{b.phoneNumber}</div>}
            {b.notes && <div className="small mt-2">Notes: {b.notes}</div>}

            {address && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Delivering to</h6>
                <div className="small">
                  Address: {address}
                  {' · '}
                  <MapLink address={b.deliveryAddress} fallbackText={address} title="Delivery address" label="📍 map" />
                </div>
              </>
            )}

            {b.ownerName && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Owner</h6>
                <div className="small">
                  {[b.ownerName, b.ownerEmail, b.ownerPhone].filter(Boolean).join(' · ')}
                </div>
              </>
            )}
          </div>

          <div className="col-md-6">
            {pay && (
              <>
                <h6 className="text-uppercase text-muted small">Payment</h6>
                <div className="small mb-1">
                  Status:{' '}
                  <span className={`fw-semibold text-${PAYMENT_STATE_VARIANT[pay.state] || 'secondary'}`}>
                    {pay.label}
                  </span>
                  <InfoTip text={PAYMENT_STATE_HELP[pay.state]} />
                </div>
                {(pay.received || pay.amountReceived > 0) && (pay.method || pay.receivedAt) && (
                  // tax_and_gst_calculation.md - how and when the payment was made, shown to the
                  // customer for their own booking and to the owner/website owner.
                  <div className="small fw-semibold">
                    {pay.method ? `Paid via ${pay.method.replace(/_/g, ' ')}` : 'Paid'}
                    {pay.receivedAt && ` on ${new Date(pay.receivedAt).toLocaleString()}`}
                  </div>
                )}
                {pay.amount != null && <div className="small">Order amount: {money(pay.amount)}</div>}
                {pay.amountReceived != null && <div className="small">Received: {money(pay.amountReceived)}</div>}
                {pay.amountDue != null && pay.amountDue > 0 && (
                  <div className="small text-warning">Still receivable: {money(pay.amountDue)}</div>
                )}
                {pay.amountRefunded > 0 && <div className="small">Refunded: {money(pay.amountRefunded)}</div>}
                {pay.method && <div className="small">Method: {pay.method.replace(/_/g, ' ')}</div>}
                {pay.reference && <div className="small">Reference: <span className="font-monospace">{pay.reference}</span></div>}
                {pay.collectionSource && (
                  <div className="small">Collected: {COLLECTION_SOURCE_LABEL[pay.collectionSource] || pay.collectionSource}</div>
                )}
                {pay.refundResponsibility && (
                  <div className="small">Refunds handled by: {REFUND_RESPONSIBILITY_LABEL[pay.refundResponsibility] || pay.refundResponsibility}</div>
                )}
                {b.payoutStatus && b.payoutStatus !== 'NOT_DUE' && (
                  // The customer's payment (above) and the platform's payout to the owner are two
                  // separate money movements - spell that out so "Payment received" + a pending
                  // payout doesn't look like a contradiction.
                  <div className="small">
                    Owner payout (platform &rarr; rental owner): {b.payoutStatus.replace(/_/g, ' ').toLowerCase()}
                    {b.payoutStatus === 'PENDING' && (
                      <span className="text-muted"> — sent to the owner after the rental completes.</span>
                    )}
                  </div>
                )}
                {pay.receivedAt && <div className="small text-muted">Received {new Date(pay.receivedAt).toLocaleString()}</div>}
                {pay.receipt && <div className="small text-muted font-monospace">{pay.receipt}</div>}
                {(pay.gatewayOrderId || pay.gatewayPaymentId) && (
                  <div className="small text-muted font-monospace">
                    {[pay.gatewayOrderId, pay.gatewayPaymentId].filter(Boolean).join(' · ')}
                  </div>
                )}
              </>
            )}

            {hasCharges && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Charges</h6>
                <Table size="sm" className="mb-0">
                  <tbody>
                    {b.rentalAmount != null && (
                      <tr><td>Rental amount</td><td className="text-end">{money(b.rentalAmount)}</td></tr>
                    )}
                    {b.platformFee != null && (
                      <tr><td>Platform fee</td><td className="text-end">{money(b.platformFee)}</td></tr>
                    )}
                    {b.deliveryCharge != null && (
                      <tr>
                        <td>
                          Delivery charge
                          {deliveryChargeNote(b) && <span className="text-muted"> · {deliveryChargeNote(b)}</span>}
                        </td>
                        <td className="text-end">{money(b.deliveryCharge)}</td>
                      </tr>
                    )}
                    {b.depositAmount > 0 && (
                      <tr><td>Security deposit</td><td className="text-end">{money(b.depositAmount)}</td></tr>
                    )}
                    {b.totalAmount != null && (
                      <tr className="fw-bold border-top">
                        <td>Total payable</td><td className="text-end">{money(b.totalAmount)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {hasBusiness && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Business breakdown</h6>
                <div className="small text-muted mb-1">Internal — not shown to the customer.</div>
                <Table size="sm" className="mb-0">
                  <tbody>
                    {b.ownerEarnings != null && (
                      <tr><td>Rental owner earnings</td><td className="text-end">{money(b.ownerEarnings)}</td></tr>
                    )}
                    {(b.platformCommission != null || b.commissionRate != null) && (
                      <tr>
                        <td>Booking commission{b.commissionRate != null ? ` (${Math.round(b.commissionRate * 100)}%)` : ''}</td>
                        <td className="text-end">
                          {b.platformCommission != null
                            ? money(b.platformCommission)
                            : money((b.rentalAmount || 0) * b.commissionRate)}
                        </td>
                      </tr>
                    )}
                    {b.platformFee != null && (
                      <tr><td>Platform fee (charged to customer)</td><td className="text-end">{money(b.platformFee)}</td></tr>
                    )}
                    {(b.platformCommission != null && b.platformFee != null) && (
                      <tr className="fw-bold border-top">
                        <td>Website business total</td>
                        <td className="text-end">{money(b.platformCommission + b.platformFee)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        </div>

        {tax && (tax.lines?.length > 0) && (
          <>
            <div className="mt-3 d-flex align-items-center gap-2">
              <h6 className="text-uppercase text-muted small mb-0">Tax &amp; invoice</h6>
              <Button
                size="sm" variant="link" className="p-0 align-baseline small"
                onClick={() => setShowTax((v) => !v)}
                aria-expanded={showTax}
              >
                {showTax ? 'Hide details' : 'Show details'}
              </Button>
            </div>
            <Collapse in={showTax}>
              <div>
            <div className="small text-muted mb-1 mt-1">
              {tax.invoiceNumber && <>Invoice {tax.invoiceNumber} &middot; </>}
              {tax.supplyType === 'INTER_STATE' ? 'Inter-state (IGST)' : 'Intra-state (CGST + SGST)'}
              {tax.placeOfSupplyStateCode && <> &middot; Place of supply {tax.placeOfSupplyStateCode}</>}
              {tax.platformGstin && <> &middot; GSTIN {tax.platformGstin}</>}
              {tax.pricesAreTaxInclusive && <> &middot; prices are tax-inclusive</>}
            </div>
            <Table size="sm" className="mb-0" responsive>
              <thead>
                <tr>
                  <th>Component</th>
                  <th className="text-end">Taxable</th>
                  <th className="text-end">GST</th>
                  <th className="text-end">Gross</th>
                </tr>
              </thead>
              <tbody>
                {tax.lines.map((l, i) => (
                  <tr key={l.component || i}>
                    <td>
                      {TAX_LINE_LABEL[l.component] || l.component}
                      {l.gstRate != null && <span className="text-muted"> @ {Math.round(l.gstRate * 100)}%</span>}
                      {l.sac && <div className="text-muted">SAC {l.sac}</div>}
                    </td>
                    <td className="text-end">{money(l.taxableValue)}</td>
                    <td className="text-end">{gstOf(l)}</td>
                    <td className="text-end">{money(l.grossValue)}</td>
                  </tr>
                ))}
                {tax.customerGst && (
                  <tr className="fw-bold border-top">
                    <td>Customer GST total</td>
                    <td className="text-end">{money(tax.customerGst.taxableValue)}</td>
                    <td className="text-end">{money(tax.customerGst.gstTotal)}</td>
                    <td className="text-end">{money(tax.customerGst.taxableValue + tax.customerGst.gstTotal)}</td>
                  </tr>
                )}
              </tbody>
            </Table>
            {tax.deposit && (
              <div className="small text-muted mt-1">
                Security deposit {money(tax.deposit.amount)} — refundable, not a supply, no GST.
              </div>
            )}
            {(tax.tcs?.amount > 0 || tax.tds?.amount > 0) && (
              <div className="small mt-1">
                {tax.tcs?.amount > 0 && <>TCS (GST s.52) {money(tax.tcs.amount)} @ {Math.round(tax.tcs.rate * 100)}%. </>}
                {tax.tds?.amount > 0 && <>TDS (IT s.194-O) {money(tax.tds.amount)} @ {Math.round(tax.tds.rate * 100)}%.</>}
              </div>
            )}
            {tax.ownerPayout && (
              <div className="small mt-1">
                Owner payout: {money(tax.ownerPayout.gross)} gross &middot; {money(tax.ownerPayout.netOfTds)} net of TDS.
              </div>
            )}
            {tax.profitLoss && (
              <>
                <div className="small text-muted mt-2 mb-1">Platform profit / loss — internal.</div>
                <Table size="sm" className="mb-0">
                  <tbody>
                    <tr><td>Gross revenue (fee + commission)</td><td className="text-end">{money(tax.profitLoss.platformGrossRevenue)}</td></tr>
                    <tr><td>Output GST to remit</td><td className="text-end">{money(tax.profitLoss.platformOutputGst)}</td></tr>
                    <tr><td>Est. gateway fee ({Math.round((tax.profitLoss.gatewayFeeRate || 0) * 100)}%)</td><td className="text-end">−{money(tax.profitLoss.estimatedGatewayFee)}</td></tr>
                    <tr className="fw-bold border-top"><td>Net margin</td><td className="text-end">{money(tax.profitLoss.platformNetMargin)}</td></tr>
                  </tbody>
                </Table>
              </>
            )}
              </div>
            </Collapse>
          </>
        )}

        <h6 className="text-uppercase text-muted small mt-3">Fulfillment</h6>
        {b.deliveryStatus && (
          <div className="small">
            Delivery status: {b.deliveryStatus.replace(/_/g, ' ')}
            <InfoTip text={DELIVERY_STATUS_HELP[b.deliveryStatus]} />
          </div>
        )}
        {b.returnStatus && (
          <div className="small">
            Return status: {b.returnStatus.replace(/_/g, ' ')}
            <InfoTip text={RETURN_STATUS_HELP[b.returnStatus]} />
          </div>
        )}
        {b.deliveryTicket && (
          <DeliveryTicketPanel ticket={b.deliveryTicket} className="mt-1" />
        )}
        {b.deliveryAssignment?.assignedTo && (
          <div className="small text-muted">
            Assigned to {b.deliveryAssignment.assignedTo.name || '—'}
            {b.deliveryAssignment.scheduledAt && ` (${new Date(b.deliveryAssignment.scheduledAt).toLocaleString()})`}
          </div>
        )}
        {b.deliveryAssignment?.notes && (
          <div className="small text-muted">Delivery notes: {b.deliveryAssignment.notes}</div>
        )}
        {b.deliveryNeedsReassignment && (
          <div className="small"><Badge bg="warning" text="dark">Needs reassignment</Badge></div>
        )}
        {b.damageReport?.reported && (
          <div className="small text-warning mt-1">
            Damage reported{b.damageReport.notes ? `: ${b.damageReport.notes}` : ''}
          </div>
        )}

        <h6 className="text-uppercase text-muted small mt-3">Status timeline</h6>
        <BookingTimeline booking={b} />

        {onViewProduct && (
          <Button size="sm" variant="outline-secondary" className="mt-3" onClick={onViewProduct}>
            View product
          </Button>
        )}
      </Modal.Body>
    </Modal>
  );
}
